import { useCallback, useRef, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  Heading,
  HStack,
  Link,
  Skeleton,
  Text,
  Tooltip,
  IconButton,
  VStack,
  Flex,
} from '@chakra-ui/react';
import { mdiAccountMinus } from '@mdi/js';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { IMemberTeam } from 'features/members/models';
import { removeMemberTeamFromCache } from 'features/members/utils/memberGroupsCache';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { useRemoveMembers } from 'features/user-group/user-group-tabs/hooks/useRemoveMembers';
import { RemoveMemberConfirmationModal } from 'features/user-group/user-group-tabs/components/RemoveMemberConfirmationModal';
import { useIsOverflowing } from 'features/common/hooks/useIsOverflowing';

interface TeamItemProps {
  team: IMemberTeam;
  onTeamClick: (teamId: string) => void;
  onRemoveClick: (
    e: MouseEvent<HTMLButtonElement>,
    teamId: string,
    teamName: string,
  ) => void;
}

const TeamItem = ({ team, onTeamClick, onRemoveClick }: TeamItemProps) => {
  const nameRef = useRef<HTMLAnchorElement | null>(null);
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);
  const nameOverflows = useIsOverflowing(nameRef, 'horizontal', team.name);
  const descriptionOverflows = useIsOverflowing(
    descriptionRef,
    'vertical',
    team.description ?? '',
  );
  return (
    <Box
      p='4'
      borderWidth='1px'
      borderRadius='md'
      _hover={{ bg: 'gray.50' }}
      cursor='pointer'
      onClick={() => onTeamClick(team.id)}
      data-testid={`team-item-${team.id}`}
    >
      <Flex justifyContent='space-between' alignItems='flex-start'>
        <Box flex='1' minW='0'>
          <HStack spacing='2' mb='2' align='baseline'>
            <Tooltip
              label={team.name}
              placement='top'
              isDisabled={!nameOverflows}
            >
              <Link
                ref={nameRef}
                fontWeight='medium'
                isTruncated
                onClick={(e) => {
                  e.stopPropagation();
                  onTeamClick(team.id);
                }}
                _hover={{ textDecoration: 'underline' }}
              >
                {team.name}
              </Link>
            </Tooltip>
            {team.accessLevel && (
              <Text fontSize='sm' color='gray.600' fontWeight='normal'>
                {team.accessLevel}
              </Text>
            )}
          </HStack>
          {team.description && (
            <Tooltip
              label={team.description}
              placement='top'
              isDisabled={!descriptionOverflows}
            >
              <Text
                ref={descriptionRef}
                fontSize='sm'
                color='gray.600'
                noOfLines={2}
              >
                {team.description}
              </Text>
            </Tooltip>
          )}
        </Box>
        <Tooltip label='Remove user from group'>
          <IconButton
            aria-label='Remove user from group'
            icon={<PortalIcon path={mdiAccountMinus} />}
            size='sm'
            variant='ghost'
            colorScheme='red'
            onClick={(e) => onRemoveClick(e, team.id, team.name)}
            data-testid={`remove-from-group-button-${team.id}`}
          />
        </Tooltip>
      </Flex>
    </Box>
  );
};

interface IMemberTeamsSectionProps {
  teams: IMemberTeam[];
  isLoading: boolean;
  hasResolvedTeams: boolean;
  memberId: string;
  memberName: string;
  onTeamRemoved: (teamId: string) => void;
  onAddToTeamsClick?: () => void;
  showAddButton?: boolean;
}

export const MemberTeamsSection = ({
  teams,
  isLoading,
  hasResolvedTeams,
  memberId,
  memberName,
  onTeamRemoved,
  onAddToTeamsClick,
  showAddButton = false,
}: IMemberTeamsSectionProps) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const organizationId = new URLSearchParams(history.location.search).get(
    'organization',
  );
  const groupsRegionRef = useRef<HTMLDivElement | null>(null);
  const [removingTeam, setRemovingTeam] = useState<{
    teamId: string;
    teamName: string;
  } | null>(null);

  const handleRemoveSuccess = useCallback(() => {
    const teamId = removingTeam?.teamId;
    if (teamId) {
      try {
        removeMemberTeamFromCache({ organizationId, memberId, teamId });
      } catch {
        // Ignore cache issues
      }

      queryClient.invalidateQueries(['organization/members']);
      onTeamRemoved(teamId);
    }
    setRemovingTeam(null);
  }, [
    removingTeam?.teamId,
    memberId,
    organizationId,
    queryClient,
    onTeamRemoved,
  ]);

  const {
    isOpen,
    membersToRemove,
    handleOpen,
    handleClose,
    handleConfirm,
    isLoading: isRemoving,
  } = useRemoveMembers({
    groupId: removingTeam?.teamId ?? '',
    onSuccess: handleRemoveSuccess,
  });

  const handleTeamClick = useCallback(
    (teamId: string) => {
      history.push(
        `/admin/user-groups/${teamId}/members/page/1${history.location.search}`,
      );
    },
    [history],
  );

  const handleRemoveClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>, teamId: string, teamName: string) => {
      e.stopPropagation();
      const [givenName, ...rest] = memberName.split(' ');
      const familyName = rest.join(' ');

      setRemovingTeam({ teamId, teamName });
      handleOpen({
        id: memberId,
        email: '',
        company: '',
        givenName,
        familyName,
      });
    },
    [memberId, memberName, handleOpen],
  );

  const handleModalClose = useCallback(() => {
    handleClose();
    setRemovingTeam(null);
  }, [handleClose]);

  if (isLoading) {
    return (
      <Box>
        <Heading size='md' mb='4'>
          Groups
        </Heading>
        <Skeleton height='20' mb='2' />
        <Skeleton height='20' mb='2' />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justifyContent='space-between' alignItems='center' mb='4'>
        <Heading size='md'>Groups</Heading>
        {showAddButton && onAddToTeamsClick && (
          <Button
            variant='link'
            colorScheme='primary'
            onClick={onAddToTeamsClick}
            data-testid='add-to-teams-button'
            size='sm'
          >
            Add to groups
          </Button>
        )}
      </Flex>
      <Box ref={groupsRegionRef} tabIndex={-1}>
        {teams.length === 0 ? (
          hasResolvedTeams ? (
            <Text data-testid='no-teams-message' fontSize='sm' color='gray.600'>
              This user is not assigned to any groups.
            </Text>
          ) : (
            <Box>
              <Skeleton height='20' mb='2' />
              <Skeleton height='20' mb='2' />
            </Box>
          )
        ) : (
          <VStack spacing='3' align='stretch'>
            {teams.map((team) => (
              <TeamItem
                key={team.id}
                team={team}
                onTeamClick={handleTeamClick}
                onRemoveClick={handleRemoveClick}
              />
            ))}
          </VStack>
        )}
      </Box>
      {isOpen && (
        <RemoveMemberConfirmationModal
          groupName={removingTeam?.teamName ?? ''}
          isOpen={isOpen}
          membersId={membersToRemove}
          onSuccess={handleConfirm}
          onClose={handleModalClose}
          isLoading={isRemoving}
          finalFocusRef={groupsRegionRef}
        />
      )}
    </Box>
  );
};
